import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const SITE = 'https://banczik.com';
const NAME = 'Samuel Banczík';
const TWITTER = '@ssmzxk';

const PROFILES = [
  'https://github.com/SamuelBanczik',
  'https://www.linkedin.com/in/samuelbanczik/',
  'https://www.instagram.com/ssmzxk',
  'https://x.com/ssmzxk',
];

const SEO = ({
  title = `${NAME} | Software Engineer & Startup Founder`,
  description = 'Samuel Banczík is a software engineer and startup founder. He designs the brand, writes the code and ships AI-powered web products to production.',
  keywords = 'Samuel Banczík, Samuel Banczik, fullstack developer, software engineer, React developer, Next.js, startup founder, entrepreneur, Slovakia, FindMeFont, SSOSTA AI, HeatTrack',
  ogImage = `${SITE}/images/banczik-share.jpg`,
  ogImageAlt = `${NAME} — software engineer and startup founder`,
  ogImageWidth = '1200',
  ogImageHeight = '630',
  url = `${SITE}/`,
  type = 'profile',
  author = NAME,
  twitterHandle = TWITTER,
  robots = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
  structuredData = null,
}) => {
  useEffect(() => {
    document
      .querySelectorAll('head [data-seo="static"]')
      .forEach((el) => el.remove());
  }, []);

  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': `${SITE}/#person`,
    name: NAME,
    alternateName: ['Samuel Banczik', 'Banczík Samuel', 'Banczik Samuel'],
    url: `${SITE}/`,
    image: `${SITE}/images/banczik.jpg`,
    email: 'mailto:samuel.banczik@gmail.com',
    jobTitle: 'Fullstack Developer, Software Engineer & Startup Founder',
    description,
    sameAs: PROFILES,
    knowsAbout: [
      'Fullstack Development', 'Software Engineering', 'JavaScript', 'TypeScript',
      'React', 'Next.js', 'Node.js', 'Python', 'PHP', 'MySQL', 'MongoDB',
      'OpenAI API', 'AI Integration', 'UI/UX Design', 'Graphic Design',
      'Entrepreneurship', 'Startups',
    ],
    knowsLanguage: ['sk', 'en'],
    nationality: { '@type': 'Country', name: 'Slovakia' },
    address: { '@type': 'PostalAddress', addressCountry: 'SK' },
    affiliation: {
      '@type': 'CollegeOrUniversity',
      name: 'Brno University of Technology (VUT)',
      alternateName: 'Vysoké učení technické v Brně',
      address: { '@type': 'PostalAddress', addressLocality: 'Brno', addressCountry: 'CZ' },
    },
    alumniOf: {
      '@type': 'EducationalOrganization',
      name: 'Tatranská akadémia',
      description:
        'a private high school in Slovakia. Field of study: Computer science and intelligent technologies.',
      address: { '@type': 'PostalAddress', addressCountry: 'SK' },
    },
  };

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en" />
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={url} />
      <link rel="alternate" hrefLang="en" href={url} />
      <link rel="alternate" hrefLang="x-default" href={url} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:secure_url" content={ogImage} />
      <meta property="og:image:width" content={ogImageWidth} />
      <meta property="og:image:height" content={ogImageHeight} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:locale:alternate" content="sk_SK" />
      <meta property="og:site_name" content={NAME} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={twitterHandle} />
      <meta name="twitter:creator" content={twitterHandle} />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      <script type="application/ld+json">
        {JSON.stringify(structuredData || defaultStructuredData)}
      </script>
    </Helmet>
  );
};

export default SEO;
